import Link from 'next/link';
import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import Price from '~/components/atoms/Price';
import { modelPropertyType } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { cn } from '~/lib/utils/classname';
import {
  formatPropertyType,
  getMainPropertyCategory,
  getPropertyPrice,
  propertyIsRent,
} from '~/lib/utils/properties';
import { notNullable } from '~/lib/utils/runtime-type';

import Tag from '../../atoms/Tag';

export type Props = {
  side: 'left' | 'right';
  showPriceDetails?: boolean;
} & z.infer<
  typeof modelPropertyType.schema
>['propertyType']['properties']['edges'][number]['node'];

const PropertyTeaseAside: React.FC<Props> = ({
  side,
  showPriceDetails = false,
  ...property
}) => {
  const { title, propertyMeta } = property;

  const mainCategorySlug = getMainPropertyCategory(property);

  const secondaryTitle = [formatPropertyType(propertyMeta), propertyMeta.city]
    .filter(Boolean)
    .join(' — ');

  const price = getPropertyPrice(property);
  const hidePrices = property.propertyMeta.priceHidden;

  return (
    <aside
      className={cn(
        'relative row-start-1 flex h-full w-full flex-col justify-between gap-y-6 p-4 pb-5 lg:w-[320px] lg:gap-y-0 lg:p-10 lg:pb-20',
        {
          'col-start-1': side === 'left',
          'col-start-3 items-end max-lg:hidden': side === 'right',
        }
      )}
      itemScope
      itemType="http://schema.org/Offer"
    >
      <div
        className={cn('flex flex-col gap-y-2', {
          'items-end': side === 'right',
        })}
      >
        {(propertyMeta.tagline || propertyMeta.status) && (
          <div
            className={cn('mb-4 flex gap-2 lg:mb-6', {
              'justify-end': side === 'right',
            })}
          >
            {propertyMeta.tagline && (
              <div className="relative">
                <Tag variant="dark">{propertyMeta.tagline}</Tag>
              </div>
            )}

            {propertyMeta.status && (
              <div className="relative">
                <Tag variant="dark">{propertyMeta.status}</Tag>
              </div>
            )}
          </div>
        )}

        {secondaryTitle && (
          <div className="text-brand-quinary text-sm">{secondaryTitle}</div>
        )}

        <Heading
          as="h2"
          level={4}
          className={cn('text-brand-secondary', {
            'text-end': side === 'right',
          })}
          itemProp="name"
        >
          {title}
        </Heading>

        {notNullable(price) && !hidePrices && (
          <Price
            value={price}
            supText={propertyIsRent(property) ? 'CC' : null}
            className="text-brand-quinary relative text-base"
          />
        )}

        {showPriceDetails && !hidePrices && (
          <div
            className={cn('text-brand-primary mt-4 flex flex-col gap-1', {
              'justify-end text-end': side === 'right',
            })}
          >
            {propertyIsRent(property) ? (
              <>
                {notNullable(property.propertyMeta.priceCharges) && (
                  <Price
                    className={cn('flex flex-row gap-1 text-sm', {
                      'justify-end text-end': side === 'right',
                    })}
                    value={property.propertyMeta.priceCharges}
                    beforeText={
                      <span className="text-brand-quinary">Charges</span>
                    }
                  />
                )}

                {notNullable(property.propertyMeta.priceFees) && (
                  <Price
                    className={cn('flex flex-row gap-1 text-sm', {
                      'justify-end text-end': side === 'right',
                    })}
                    value={property.propertyMeta.priceFees}
                    beforeText={
                      <span className="text-brand-quinary">Honoraires</span>
                    }
                    supText="TTC"
                  />
                )}

                {notNullable(property.propertyMeta.priceFees) &&
                  notNullable(property.propertyMeta.priceInventoryFees) && (
                    <Price
                      className={cn('flex flex-row gap-1 text-sm', {
                        'justify-end text-end': side === 'right',
                      })}
                      value={property.propertyMeta.priceInventoryFees}
                      beforeText={
                        <span className="text-brand-quinary">
                          — dont état des lieux
                        </span>
                      }
                      supText="TTC"
                    />
                  )}

                {notNullable(property.propertyMeta.priceFees) &&
                  notNullable(property.propertyMeta.priceInventoryFees) && (
                    <Price
                      className={cn('flex flex-row gap-1 text-sm', {
                        'justify-end text-end': side === 'right',
                      })}
                      value={
                        property.propertyMeta.priceFees -
                        property.propertyMeta.priceInventoryFees
                      }
                      beforeText={
                        <span className="text-brand-quinary">
                          — dont visite, dossier
                        </span>
                      }
                      supText="TTC"
                    />
                  )}

                {notNullable(property.propertyMeta.priceDeposit) && (
                  <Price
                    className={cn('flex flex-row gap-1 text-sm', {
                      'justify-end text-end': side === 'right',
                    })}
                    value={property.propertyMeta.priceDeposit}
                    beforeText={
                      <span className="text-brand-quinary">
                        Dépôt de garantie
                      </span>
                    }
                  />
                )}
              </>
            ) : (
              <>
                {notNullable(property.propertyMeta.priceSaleWithoutFees) &&
                  property.propertyMeta.priceSaleWithoutFees !==
                    property.propertyMeta.priceSale && (
                    <Price
                      className={cn('flex flex-row gap-1 text-sm', {
                        'justify-end text-end': side === 'right',
                      })}
                      value={property.propertyMeta.priceSaleWithoutFees}
                      beforeText={
                        <span className="text-brand-quinary">Hors charges</span>
                      }
                    />
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {mainCategorySlug && (
        <div className="flex">
          <CallToAction
            level={2}
            as={Link}
            href={routeGenerator.property({
              category: mainCategorySlug,
              slug: property.slug,
            })}
          >
            Découvrir
          </CallToAction>
        </div>
      )}
    </aside>
  );
};

export default PropertyTeaseAside;
